import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FolderOpen, Plus, Image, Loader2, Check, X, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface NFT {
  id: string;
  name: string;
  image_url: string | null;
  collection_name: string | null;
}

interface CollectionManagerProps {
  walletAddress: string;
  onCollectionChange?: () => void;
  refreshKey?: number; // Force refresh when this changes
}

export function CollectionManager({ walletAddress, onCollectionChange, refreshKey }: CollectionManagerProps) {
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [selectedNft, setSelectedNft] = useState<string>("");
  const [selectedCollection, setSelectedCollection] = useState<string>("");
  const [assigning, setAssigning] = useState(false);

  const MAX_COLLECTION_LENGTH = 50;

  useEffect(() => {
    fetchData();
  }, [walletAddress, refreshKey]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch user's NFTs (only created by them)
      const { data: nftData } = await supabase
        .from("nfts")
        .select("id, name, image_url, collection_name")
        .eq("creator_wallet", walletAddress)
        .order("created_at", { ascending: false });

      const userNfts = (nftData || []) as NFT[];
      setNfts(userNfts);

      // Extract unique collections
      const uniqueCollections = Array.from(
        new Set(userNfts.filter(n => n.collection_name).map(n => n.collection_name!))
      );
      setCollections(uniqueCollections);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const createCollection = () => {
    const name = newCollectionName.trim();
    if (!name) {
      toast({ title: "Enter a collection name", variant: "destructive" });
      return;
    }
    if (name.length > MAX_COLLECTION_LENGTH) {
      toast({ title: `Max ${MAX_COLLECTION_LENGTH} characters`, variant: "destructive" });
      return;
    }
    if (collections.includes(name)) {
      toast({ title: "Collection already exists", variant: "destructive" });
      return;
    }

    setCollections([...collections, name]);
    setNewCollectionName("");
    toast({ title: `Collection "${name}" created` });
  };

  const assignNftToCollection = async () => {
    if (!selectedNft || !selectedCollection) {
      toast({ title: "Select an NFT and a collection", variant: "destructive" });
      return;
    }

    setAssigning(true);
    try {
      const { error } = await supabase
        .from("nfts")
        .update({ collection_name: selectedCollection })
        .eq("id", selectedNft)
        .eq("creator_wallet", walletAddress);

      if (error) throw error;

      // Update local state
      setNfts(nfts.map(n => 
        n.id === selectedNft ? { ...n, collection_name: selectedCollection } : n
      ));

      const nftName = nfts.find(n => n.id === selectedNft)?.name;
      toast({ title: `"${nftName}" added to "${selectedCollection}"` });
      
      setSelectedNft("");
      setSelectedCollection("");
      onCollectionChange?.();
    } catch (error) {
      console.error("Error assigning NFT:", error);
      toast({ title: "Failed to assign NFT", variant: "destructive" });
    }
    setAssigning(false);
  };

  const removeFromCollection = async (nftId: string) => {
    try {
      const { error } = await supabase
        .from("nfts")
        .update({ collection_name: null })
        .eq("id", nftId)
        .eq("creator_wallet", walletAddress);

      if (error) throw error;

      setNfts(nfts.map(n => 
        n.id === nftId ? { ...n, collection_name: null } : n
      ));

      toast({ title: "NFT removed from collection" });
      onCollectionChange?.();
    } catch (error) {
      console.error("Error removing NFT:", error);
      toast({ title: "Failed to remove NFT", variant: "destructive" });
    }
  };

  // NFTs without a collection
  const unassignedNfts = nfts.filter(n => !n.collection_name);

  if (loading) {
    return (
      <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-primary" />
          My Collections
          <span className="ml-auto text-sm font-normal text-muted-foreground">
            {collections.length} {collections.length === 1 ? "collection" : "collections"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Create New Collection */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Create New Collection</label>
          <div className="flex gap-2">
            <Input
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              placeholder="e.g. My Art Series"
              className="bg-background/50"
              maxLength={MAX_COLLECTION_LENGTH + 5}
            />
            <Button 
              onClick={createCollection} 
              size="sm"
              disabled={!newCollectionName.trim() || newCollectionName.length > MAX_COLLECTION_LENGTH}
            >
              <Plus className="w-4 h-4 mr-1" />
              Create
            </Button>
          </div>
          {newCollectionName.length > MAX_COLLECTION_LENGTH && (
            <p className="text-xs text-destructive">Max {MAX_COLLECTION_LENGTH} characters</p>
          )}
        </div>

        {/* Assign NFT to Collection */}
        {collections.length > 0 && unassignedNfts.length > 0 && (
          <div className="space-y-3 pt-4 border-t border-border/50">
            <label className="text-sm font-medium">Add NFT to Collection</label>
            <div className="grid gap-2 sm:grid-cols-[1fr,1fr,auto]">
              <Select value={selectedNft} onValueChange={setSelectedNft}>
                <SelectTrigger className="bg-background/50">
                  <SelectValue placeholder="Select NFT" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600 z-50">
                  {unassignedNfts.map((nft) => (
                    <SelectItem 
                      key={nft.id} 
                      value={nft.id}
                      className="text-white hover:bg-primary/30 focus:bg-primary/40 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        {nft.image_url ? (
                          <img src={nft.image_url} alt="" className="w-5 h-5 rounded object-cover" />
                        ) : (
                          <Image className="w-5 h-5 text-muted-foreground" />
                        )}
                        <span className="truncate">{nft.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedCollection} onValueChange={setSelectedCollection}>
                <SelectTrigger className="bg-background/50">
                  <SelectValue placeholder="Select Collection" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600 z-50">
                  {collections.map((col) => (
                    <SelectItem 
                      key={col} 
                      value={col}
                      className="text-white hover:bg-primary/30 focus:bg-primary/40 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-primary" />
                        {col}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button 
                onClick={assignNftToCollection} 
                disabled={!selectedNft || !selectedCollection || assigning}
                size="sm"
              >
                {assigning ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Assign
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Collections Overview */}
        {collections.length > 0 && (
          <div className="space-y-3 pt-4 border-t border-border/50">
            <label className="text-sm font-medium">Your Collections</label>
            <div className="space-y-4">
              {collections.map((collectionName) => {
                const collectionNfts = nfts.filter(n => n.collection_name === collectionName);
                const isEmpty = collectionNfts.length === 0;
                return (
                  <div key={collectionName} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-primary" />
                      <span className="font-medium">{collectionName}</span>
                      <Badge variant="secondary" className="text-xs">
                        {collectionNfts.length} NFTs
                      </Badge>
                      {isEmpty && (
                        <button
                          onClick={() => {
                            setCollections(collections.filter(c => c !== collectionName));
                            toast({ title: `Collection "${collectionName}" deleted` });
                          }}
                          className="ml-auto p-1 hover:bg-destructive/20 rounded text-destructive/70 hover:text-destructive transition-colors"
                          title="Delete empty collection"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {collectionNfts.length > 0 && (
                      <div className="flex flex-wrap gap-2 pl-6">
                        {collectionNfts.map((nft) => (
                          <div 
                            key={nft.id} 
                            className="flex items-center gap-2 px-2 py-1 rounded-lg bg-muted/50 text-sm group"
                          >
                            {nft.image_url && (
                              <img src={nft.image_url} alt="" className="w-6 h-6 rounded object-cover" />
                            )}
                            <span className="truncate max-w-[100px]">{nft.name}</span>
                            <button
                              onClick={() => removeFromCollection(nft.id)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-destructive/20 rounded text-destructive"
                              title="Remove from collection"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {collections.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <FolderOpen className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No collections yet</p>
            <p className="text-xs">Create a collection to organize your NFTs</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}